import React from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TelephonySection } from 'components/SmartAssistantSections/TelephonySection/TelephonySection';
import { usePhoneSmartAssistantForms } from 'hooks/SmartAssistantForms/use-phone-smart-assistant-forms';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { IntegrationManagerService } from 'services/integration-manager-service';
import { NotificationService } from 'services/notification-service';
import { PHONE_NUMBERS_QUERY_KEY, SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface TelephonyManagementSectionProps {
  smartAssistant: SmartAssistant;
}

export function TelephonyManagementSection({ smartAssistant }: TelephonyManagementSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { telephonyForm } = usePhoneSmartAssistantForms(smartAssistant);

  const updateIntegrationsMutation = useMutation({
    mutationFn: IntegrationManagerService.updateSmartAssistantIntegrations,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      telephonyForm.resetDirty();
      NotificationService.showResult(true, t('FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_PHONE_NUMBER'));
      queryClient.invalidateQueries({ queryKey: [PHONE_NUMBERS_QUERY_KEY] });

      if (updatedSmartAssistant.voiceIntegration?.telephonyConfiguration) {
        telephonyForm.setFieldValue(
          'telephonyConfiguration',
          updatedSmartAssistant.voiceIntegration.telephonyConfiguration
        );
      }
    },
    onError: () => {
      NotificationService.showResult(false, t('FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_PHONE_NUMBER'));
    }
  });

  const onSave = () => {
    const validation = telephonyForm.validate();
    if (validation.hasErrors || !smartAssistant.voiceIntegration) return;

    updateIntegrationsMutation.mutate({
      smartAssistantId: smartAssistant._id,
      voiceIntegration: {
        telephonyConfiguration: telephonyForm.values.telephonyConfiguration,
        // The configuration below are not updated by the user in this section, we keep the existing values
        transcriptionConfiguration: smartAssistant.voiceIntegration.transcriptionConfiguration,
        voiceConfiguration: smartAssistant.voiceIntegration.voiceConfiguration,
        deployConfiguration: smartAssistant.voiceIntegration.deployConfiguration
      }
    });
  };

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.TITLE')}
      hasChanges={telephonyForm.isDirty()}
      loading={updateIntegrationsMutation.isLoading}
      onSave={onSave}
      onCancel={() => telephonyForm.reset()}>
      <TelephonySection form={telephonyForm} mt="lg" />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
